import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Alert } from '@/components/ui';
import { Invoice, InvoiceCreateData, InvoiceUpdateData, InvoiceStatus, InvoiceAttachment } from '../../../types/Invoice';
import { parseCalendarDate } from '../../../utils/dateUtils';

interface InvoiceModalProps {
  visible: boolean;
  onClose: () => void;
  onSave: (invoiceData: InvoiceCreateData | InvoiceUpdateData, file?: File) => Promise<void>;
  invoice?: Invoice;
  projectId: number;
}

const InvoiceModal: React.FC<InvoiceModalProps> = ({
  visible,
  onClose,
  onSave,
  invoice,
  projectId
}) => {
  const [formData, setFormData] = useState<InvoiceCreateData>({
    invoiceDate: '',
    invoiceValue: '',
    invoiceNumber: '',
    status: 'New',
    comments: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [existingAttachment, setExistingAttachment] = useState<InvoiceAttachment | null>(null);

  const isEditing = !!invoice;

  useEffect(() => {
    console.log('InvoiceModal: useEffect triggered', {
      visible,
      hasInvoice: !!invoice,
      invoiceData: invoice ? {
        id: invoice.id,
        invoiceDate: invoice.invoiceDate,
        invoiceDateType: typeof invoice.invoiceDate,
        invoiceValue: invoice.invoiceValue,
        status: invoice.status
      } : null
    });

    if (visible && invoice) {
      // When editing an existing invoice, populate form with existing data.
      // The API returns invoiceDate as a UTC-midnight ISO string for what is really just a
      // calendar date. Reading it back with local getters (as this used to for both the Date
      // and string branches) shows the *previous* day in any timezone behind UTC —
      // parseCalendarDate anchors it at local midnight for the same calendar day instead, so
      // the date input pre-fills with the date the invoice was actually saved with.
      let invoiceDate = '';
      const parsedDate = parseCalendarDate(invoice.invoiceDate);
      if (parsedDate) {
        const year = parsedDate.getFullYear();
        const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
        const day = String(parsedDate.getDate()).padStart(2, '0');
        invoiceDate = `${year}-${month}-${day}`;
      }
      
      console.log('InvoiceModal: Setting invoice date for editing:', {
        originalDate: invoice.invoiceDate,
        parsedDate: invoiceDate,
        type: typeof invoice.invoiceDate
      });
      
      const newFormData = {
        invoiceDate: invoiceDate,
        invoiceValue: invoice.invoiceValue.toString(),
        invoiceNumber: invoice.invoiceNumber || '',
        status: invoice.status,
        comments: invoice.comments || ''
      };
      
      console.log('InvoiceModal: Setting form data:', newFormData);
      setFormData(newFormData);
      
      // Set existing attachment if available
      if (invoice.attachments && invoice.attachments.length > 0) {
        setExistingAttachment(invoice.attachments[0]);
      } else {
        setExistingAttachment(null);
      }
      
      setSelectedFile(null); // Clear new file selection when editing
    } else if (visible && !invoice) {
      // When creating a new invoice, set default values
      // Use local date to avoid UTC conversion issues
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      const localDateString = `${year}-${month}-${day}`;
      
      setFormData({
        invoiceDate: localDateString,
        invoiceValue: '',
        invoiceNumber: '',
        status: 'New',
        comments: ''
      });
      setSelectedFile(null);
      setExistingAttachment(null);
    }
  }, [visible, invoice]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Validate required fields
      if (!formData.invoiceDate) {
        throw new Error('Invoice Date is required');
      }
      if (!formData.invoiceValue) {
        throw new Error('Invoice Value is required');
      }
      if (!formData.status) {
        throw new Error('Status is required');
      }

      // Convert value to number
      const invoiceData = {
        ...formData,
        invoiceValue: parseFloat(formData.invoiceValue.toString())
      };

      console.log('Sending invoice data:', invoiceData);

      // Send the invoice data with file (if selected)
      await onSave(invoiceData, selectedFile || undefined);
      
      // Show success message if file was uploaded
      if (selectedFile) {
        console.log('Invoice created and file uploaded successfully');
      } else {
        console.log('Invoice created successfully');
      }
      
      onClose();
    } catch (error) {
      setError(error instanceof Error ? error.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      invoiceDate: '',
      invoiceValue: '',
      invoiceNumber: '',
      status: 'New',
      comments: ''
    });
    setError(null);
    setSelectedFile(null);
    onClose();
  };

  return (
    <Modal show={visible} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {isEditing ? 'Edit Invoice' : 'Add Invoice'}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && (
            <Alert variant="danger" onClose={() => setError(null)} dismissible>
              {error}
            </Alert>
          )}

          <Row>
            <Col md={6}>
              <Form.Group className="tw-mb-3">
                <Form.Label>Invoice Date *</Form.Label>
                <Form.Control
                  type="date"
                  name="invoiceDate"
                  value={formData.invoiceDate}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="tw-mb-3">
                <Form.Label>Invoice Value *</Form.Label>
                <Form.Control
                  type="number"
                  name="invoiceValue"
                  value={formData.invoiceValue}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  required
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="tw-mb-3">
                <Form.Label>Invoice Number (Internal)</Form.Label>
                <Form.Control
                  type="text"
                  name="invoiceNumber"
                  value={formData.invoiceNumber}
                  onChange={handleChange}
                  placeholder="Optional internal reference"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="tw-mb-3">
                <Form.Label>Status *</Form.Label>
                <Form.Select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  required
                >
                  <option value="New">New</option>
                  <option value="Finalized">Finalized</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="tw-mb-3">
            <Form.Label>Comments</Form.Label>
            <Form.Control
              as="textarea"
              name="comments"
              value={formData.comments}
              onChange={handleChange}
              rows={3}
              placeholder="Optional comments about this invoice"
            />
          </Form.Group>

          <Form.Group className="tw-mb-3">
            <Form.Label>Invoice PDF Attachment</Form.Label>
            <Form.Control
              type="file"
              accept=".pdf"
              onChange={handleFileChange}
              placeholder="Upload invoice PDF"
            />
            <Form.Text className="tw-text-content-muted">
              Only PDF files are allowed. Maximum size: 10MB
            </Form.Text>

            {/* Show existing attachment if editing */}
            {isEditing && existingAttachment && (
              <div className="tw-mt-2">
                <small className="tw-text-info">
                  <i className="bi bi-file-pdf tw-mr-1"></i>
                  Current attachment: {existingAttachment.attachmentName}
                </small>
                <div className="tw-mt-1">
                  <small className="tw-text-content-muted">
                    Upload a new file to replace the existing attachment
                  </small>
                </div>
              </div>
            )}

            {/* Show newly selected file */}
            {selectedFile && (
              <div className="tw-mt-2">
                <small className="tw-text-success">
                  <i className="bi bi-check-circle tw-mr-1"></i>
                  New file selected: {selectedFile.name}
                </small>
              </div>
            )}
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={loading || uploadingFile}>
            {loading ? 'Saving Invoice...' : uploadingFile ? 'Uploading File...' : (isEditing ? 'Update Invoice' : 'Add Invoice')}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default InvoiceModal;
